import { AbstractO2dElement, customElement, property, html } from "@o2d/shared";
import type { TemplateResult, PropertyValues } from "@o2d/shared";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { generateHTML, Editor } from "@tiptap/core";
import type { Content } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeSelection, Selection } from "@tiptap/pm/state";

import type { RichTextDocument } from "../types/rich-text-document.type.js";
import type { ImageUploadHandler } from "../types/image-upload-handler.type.js";
import type { PastedImageResolver } from "../types/pasted-image-resolver.type.js";
import {
  RICH_TEXT_CHANGED_EVENT,
  RICH_TEXT_DRAFT_RESTORED_EVENT,
  RICH_TEXT_IMAGE_CLICK_EVENT,
  type RichTextChangedEventDetail,
  type RichTextDraftRestoredEventDetail,
  type RichTextImageClickEventDetail,
} from "../types/rich-text-changed-event.types.js";
import { RICH_TEXT_EXTENSIONS } from "../controllers/rich-text-extensions.js";
import { TextCase } from "../controllers/text-case.js";
import { stripAbsoluteFontSizes } from "../controllers/paste-font-size.js";
import { DraftStore } from "../controllers/draft-store.js";
import { selectionFontSize } from "../controllers/font-size-state.js";

/**
 * `mode` of `<o2d-rich-text-editor>` — `'view'` renders the document read-only
 * via tiptap `generateHTML`; `'edit'` mounts a live tiptap `Editor` (TR4).
 */
export type RichTextEditorMode = "view" | "edit";

/**
 * A no-arg chainable tiptap toggle command bound to a toolbar button
 * (O2D.P-PRD-019). Kept as an explicit union so `chain().focus()[command]()`
 * type-checks without a cast.
 */
type ToggleCommand =
  | "toggleBold"
  | "toggleItalic"
  | "toggleUnderline"
  | "toggleStrike"
  | "toggleSuperscript"
  | "toggleSubscript"
  | "toggleBulletList"
  | "toggleOrderedList"
  | "toggleTaskList";

/** Descriptor for a toggle toolbar button (inline mark or list type). */
interface ToggleButton {
  readonly command: ToggleCommand;
  /** `editor.isActive()` name driving the button's `is-active` highlight. */
  readonly activeName: string;
  readonly title: string;
  /** Icon-only class: the button has no text content, its glyph comes from a
   *  CSS `::before` on this class — a plain-Unicode fallback in this package's
   *  `rich-text-editor.css`, a Font Awesome icon in a consumer theme (horstplan:
   *  whiteboard_rte.css). Every toggle button is icon-only since 2026-09-26 (Anke:
   *  the inline marks B/I/U/S/x²/x₂ too, not only lists). Icon-only buttons
   *  carry `aria-label` = `title`: CSS-generated content counts towards the
   *  accessible name (accname 1.2, step 2F.ii), so without it a screen reader
   *  would announce the glyph ("B", a private-use icon character) instead. */
  readonly iconClass: string;
}

/** The horizontal block-alignment values the TextAlign extension is configured for. */
type TextAlignment = "left" | "center" | "right";

/**
 * A no-arg chainable tiptap command behind a one-shot toolbar button — no
 * active state, it just runs: the table-editing commands (only valid while the
 * caret is inside a table) and the case rewrite (`TextCase`). Explicit union
 * so `chain().focus()[command]()` type-checks.
 */
type OneShotCommand =
  | "addRowAfter"
  | "deleteRow"
  | "addColumnAfter"
  | "deleteColumn"
  | "deleteTable"
  | "setUppercase"
  | "setLowercase";

/** Descriptor for a one-shot toolbar button (table editing, case rewrite). */
interface OneShotButton {
  readonly command: OneShotCommand;
  readonly title: string;
  readonly label: string;
  /** Disabled while the selection is empty — the command acts on selected
   *  text only (the case buttons). */
  readonly needsSelection?: true;
}

/** Descriptor for a text-alignment toolbar button — icon-only like every
 *  {@link ToggleButton}. */
interface AlignButton {
  readonly align: TextAlignment;
  readonly title: string;
  /** Icon-only class (see {@link ToggleButton.iconClass}) — always set for
   *  alignment buttons. */
  readonly iconClass: string;
}

// NOTE: labels/titles are hardcoded German for now (no i18n layer yet — a
// translation mechanism is planned as a follow-up). The icon-only buttons carry
// German only in their tooltips; the text labels that remain (AB/ab, the
// table-editing buttons) and the fallback glyphs in rich-text-editor.css
// (Bild / Tabelle / Linie) carry German too.

/** Inline-mark toolbar buttons (Bold / Italic / Underline / Strikethrough /
 *  Superscript / Subscript). */
const MARK_BUTTONS: readonly ToggleButton[] = [
  { command: "toggleBold", activeName: "bold", title: "Fett", iconClass: "o2d-rte-toolbar__btn--bold" },
  { command: "toggleItalic", activeName: "italic", title: "Kursiv", iconClass: "o2d-rte-toolbar__btn--italic" },
  { command: "toggleUnderline", activeName: "underline", title: "Unterstrichen", iconClass: "o2d-rte-toolbar__btn--underline" },
  { command: "toggleStrike", activeName: "strike", title: "Durchgestrichen", iconClass: "o2d-rte-toolbar__btn--strike" },
  { command: "toggleSuperscript", activeName: "superscript", title: "Hochstellen", iconClass: "o2d-rte-toolbar__btn--superscript" },
  { command: "toggleSubscript", activeName: "subscript", title: "Tiefstellen", iconClass: "o2d-rte-toolbar__btn--subscript" },
];

/** List-type toolbar buttons (Bullet / Ordered / Task). `iconClass` on the
 *  bullet/ordered/task variants swaps their old text glyph for a Font
 *  Awesome icon (whiteboard_rte.css `::before`, operator convention 2026-08-31). */
const LIST_BUTTONS: readonly ToggleButton[] = [
  { command: "toggleBulletList", activeName: "bulletList", title: "Aufzählung", iconClass: "o2d-rte-toolbar__btn--bullet-list" },
  { command: "toggleOrderedList", activeName: "orderedList", title: "Nummerierte Liste", iconClass: "o2d-rte-toolbar__btn--ordered-list" },
  { command: "toggleTaskList", activeName: "taskList", title: "Aufgabenliste", iconClass: "o2d-rte-toolbar__btn--task-list" },
];

/** Block-alignment toolbar buttons (Left / Center / Right) — always icon-only. */
const ALIGN_BUTTONS: readonly AlignButton[] = [
  { align: "left", title: "Linksbündig", iconClass: "o2d-rte-toolbar__btn--align-left" },
  { align: "center", title: "Zentriert", iconClass: "o2d-rte-toolbar__btn--align-center" },
  { align: "right", title: "Rechtsbündig", iconClass: "o2d-rte-toolbar__btn--align-right" },
];

/** Case-rewrite toolbar buttons (Anke, 2026-09-23): rewrite the selected
 *  characters, like Shift+F3 in Word — see `controllers/text-case.ts`. Text
 *  labels, not icons: Font Awesome has no upper/lower-case glyph pair. The
 *  titles match the whiteboard's optionsbar. */
const CASE_BUTTONS: readonly OneShotButton[] = [
  { command: "setUppercase", title: "Großbuchstaben", label: "AB", needsSelection: true },
  { command: "setLowercase", title: "Kleinbuchstaben", label: "ab", needsSelection: true },
];

/** Table-editing toolbar buttons — shown only while the caret is inside a table. */
const TABLE_EDIT_BUTTONS: readonly OneShotButton[] = [
  { command: "addRowAfter", title: "Zeile hinzufügen", label: "+Zeile" },
  { command: "deleteRow", title: "Zeile löschen", label: "−Zeile" },
  { command: "addColumnAfter", title: "Spalte hinzufügen", label: "+Spalte" },
  { command: "deleteColumn", title: "Spalte löschen", label: "−Spalte" },
  { command: "deleteTable", title: "Tabelle löschen", label: "⌫ Tabelle" },
];

/** Text-colour swatches (Schriftfarbe). Applied via the Color extension
 *  (`setColor` / `unsetColor`). The eleven swatches, in this order (user,
 *  2026-09-19/20): 1 #5f6b79 (grey-blue), 2 #F9B000, 3 #9E976C, 4 #AF1F4A,
 *  5 #946379, 6 #75947C, 7 #A1B200, 8 #00ACE9, 9 #618DA1, 10 #000000 (black),
 *  11 #FFFFFF (white, Anke 2026-10-03 — last, right before the "×" button
 *  that removes a colour). */
const TEXT_COLORS: readonly string[] = [
  "#5f6b79",
  "#F9B000", "#9E976C", "#AF1F4A", "#946379",
  "#75947C", "#A1B200", "#00ACE9", "#618DA1",
  "#000000", "#FFFFFF",
];

/** Highlight swatches (Hinterlegung). Applied via the Highlight extension
 *  (`setHighlight` / `unsetHighlight`). The eight brand colours, in this order
 *  (user, 2026-09-19): 1 #FFD580, 2 #C1C19D, 3 #C15F87, 4 #D5BDCA,
 *  5 #A8BAAA, 6 #CCCC60, 7 #A1DAF8, 8 #666666. Swatch 8 is a dark grey — text
 *  on it needs a light colour to stay readable. */
const HIGHLIGHT_COLORS: readonly string[] = [
  "#FFD580", "#C1C19D", "#C15F87", "#D5BDCA",
  "#A8BAAA", "#CCCC60", "#A1DAF8", "#666666",
];

/** Descriptor for one Schriftgröße step. `value` is a relative CSS size (em) so
 *  it scales with the themed base; the empty value clears the mark ("Normal").
 *  `short` is the button's visible text, `label` its full name (tooltip). */
interface FontSizeOption {
  readonly label: string;
  readonly short: string;
  readonly value: string;
}

/** Schriftgröße in vier Abstufungen (Klein / Normal / Groß / Riesig), one
 *  toolbar button each, labelled S / M / L / XL (Anke, 2026-10-03 — replaced
 *  the dropdown so a size is one click). Values are tunable; relative units
 *  keep them proportional to the base font size. */
const FONT_SIZES: readonly FontSizeOption[] = [
  { label: "Klein", short: "S", value: "0.75em" },
  { label: "Normal", short: "M", value: "" },
  { label: "Groß", short: "L", value: "1.5em" },
  { label: "Riesig", short: "XL", value: "2.5em" },
];

/** The empty ProseMirror document used when no `content` has been assigned. */
const EMPTY_DOC: RichTextDocument = { type: "doc", content: [] };

/** Debounce window (ms) for the `rich-text-changed` emit (OQ3, analog whiteboard). */
const CHANGED_DEBOUNCE_MS = 300;

/** The first image `File` in a paste event's clipboard, or null. */
function imageFileFromClipboard(event: ClipboardEvent): File | null {
  const items = event.clipboardData?.items;
  if (!items) {
    return null;
  }
  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    if (item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        return file;
      }
    }
  }
  return null;
}

/**
 * Guards an image `src` so the "inert output" guarantee holds: only http(s), an
 * image data-URL, or a same-origin relative path may become an `<img src>`.
 * Rejects `javascript:` and other active schemes (and protocol-relative `//`).
 */
function isSafeImageSrc(src: string): boolean {
  const s = src.trim();
  if (s === "") {
    return false;
  }
  if (/^https?:\/\//i.test(s)) {
    return true;
  }
  if (/^data:image\//i.test(s)) {
    return true;
  }
  // Relative / same-origin path: "/foo" (but not "//host"), "./foo" or "../foo".
  return /^\/(?!\/)/.test(s) || /^\.{1,2}\//.test(s);
}

/** Read a `File` as a base64 data-URL (fallback when no upload handler is set). */
function readFileAsDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === "string" && result.length > 0 ? result : null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * `<o2d-rich-text-editor>` — a tiptap-based rich-text viewer/editor Web Component.
 *
 * View path (§TR3): in `mode="view"` the `content` document is rendered read-only
 * through tiptap's `generateHTML` and injected into Light DOM via Lit's
 * `unsafeHTML`. The output is INERT — the shared {@link RICH_TEXT_EXTENSIONS} carry
 * no raw-HTML-passthrough extension, so `generateHTML` escapes every ProseMirror
 * text node.
 *
 * Edit path (§TR4/TR5/TR6/TR9): in `mode="edit"` a live tiptap `Editor` is mounted
 * into a Light-DOM host. Content edits are debounced (300ms) into a
 * `rich-text-changed` CustomEvent carrying the live document + a dirty flag derived
 * from a content signature vs the last saved baseline. Per-object unsaved work is
 * mirrored into `localStorage` through the {@link DraftStore} and restored on load,
 * signalled by `rich-text-draft-restored` + the {@link draftWasRestored} getter.
 *
 * `mode` is a bidirectional single-source-of-truth (§TR2a); {@link enterEditMode} /
 * {@link exitEditMode} are the imperative equivalents. `markSaved` is decoupled from
 * `mode` — the consumer orchestrates the return to view mode after a save.
 *
 * Light DOM is inherited from {@link AbstractO2dElement} (`createRenderRoot` returns
 * `this`), so global CSS and the exported structural `rich-text-editor.css` apply
 * directly and the mounted editor's `contenteditable` host is a direct descendant.
 */
@customElement("o2d-rich-text-editor")
export class O2dRichTextEditorElement extends AbstractO2dElement {
  /** Rendering mode; defaults to read-only `'view'` (TR2/TR2a). */
  @property({ type: String, reflect: true })
  mode: RichTextEditorMode = "view";

  /**
   * The ProseMirror-JSON document to render/edit. Set as a JS property (not an
   * attribute) by consumers. Lit's identity change-detection makes a reference-
   * equal re-assignment a no-op, which IS the dirty-baseline short-circuit (TR2):
   * only a genuinely new document reference rebases the baseline.
   */
  @property({ attribute: false })
  content?: RichTextDocument;

  /**
   * SID for the per-object draft key (TR2). Explicit `attribute: 'object-id'` — a
   * camelCase property would otherwise reflect onto the lowercased `objectid`
   * attribute. Without it the draft is fully disabled (defensive, TR6).
   */
  @property({ attribute: "object-id" })
  objectId?: string;

  /**
   * Host-injected uploader for pasted / picked images (Anke, 2026-07-27). When
   * set, a pasted or picked image file is uploaded through it and the returned
   * asset URL becomes the image node's `src` — so the asset lands in the host's
   * file store (e.g. the o2d file list) and the document stays lightweight. When
   * absent, images fall back to an inline base64 data-URL. Same shape as
   * `@o2d/whiteboard`'s handler, so a host can reuse one uploader for both.
   */
  @property({ attribute: false })
  imageUploadHandler?: ImageUploadHandler;

  /**
   * Host hook asked before a pasted image is uploaded, when the clipboard holds an image file
   * AND text (ticket "bild kopieren ermöglichen", 2026-10-28). The host may answer with the
   * `src` of an image it already holds — then nothing is uploaded. See
   * {@link PastedImageResolver} for the full contract.
   */
  @property({ attribute: false })
  pastedImageResolver?: PastedImageResolver;

  /** The mounted tiptap editor (edit mode only). */
  #editor?: Editor;

  /** Per-object localStorage draft store; owns the single debounced save timer. */
  readonly #draftStore = new DraftStore();

  constructor() {
    super();
    // Registered once, here, on the element itself: they survive removal and
    // re-insertion without doubling (a connectedCallback would add one per
    // insertion) and are independent of re-renders (renderView runs on each).
    this.addEventListener("click", this.#onImageClick);
    this.addEventListener("dblclick", this.#onImageDoubleClick);
  }

  /**
   * VIEW mode: report a click on an image as `rich-text-image-click`
   * (Anke, 2026-09-26). The view renders `generateHTML` straight into this Light-DOM
   * host without a wrapper, so the click target IS the `img`. Edit mode is left
   * alone here — there a single click selects the image for resizing; see
   * {@link #onImageDoubleClick}.
   */
  #onImageClick = (event: MouseEvent): void => {
    if (this.mode === "view") {
      this.#reportImageClick(event);
    }
  };

  /**
   * EDIT mode: report a DOUBLE click on an image as `rich-text-image-click`
   * (Anke, 2026-09-26). The first click of it has already selected the image —
   * that is wanted, it is how a user reaches the resize handles. A double click on
   * a handle does not count (its target is the handle, not the `img`). View mode
   * ignores double clicks: its single click has already reported the image.
   */
  #onImageDoubleClick = (event: MouseEvent): void => {
    if (this.mode === "edit") {
      this.#reportImageClick(event);
    }
  };

  /**
   * Dispatches `rich-text-image-click` for a click on an image. If the host cancels
   * the event, it has handled the click: the original event is cancelled too and
   * stopped, so clickable ancestors (a task card, a tile) do not also act on it.
   */
  #reportImageClick(event: MouseEvent): void {
    if (!(event.target instanceof HTMLImageElement)) {
      return;
    }
    const image = event.target;
    const handled = !this.fireEvent<RichTextImageClickEventDetail>(
      RICH_TEXT_IMAGE_CLICK_EVENT,
      { src: image.src, alt: image.alt },
      { cancelable: true },
    );
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /** Signature of the last saved / loaded baseline document (dirty comparison). */
  #savedContentSignature = "";

  /** True once a localStorage draft was restored for the current load (OQ4, TR2). */
  #draftWasRestored = false;

  /** Whether the current (content, objectId) load has been applied to the editor. */
  #loadApplied = false;

  /**
   * Element-local flag (TR6 (b)): user edits have happened since the last
   * flush/reset (`markSaved`, `discardDraft`, `#applyLoad`, disconnect).
   * Since the 2026-09-18 fix it does NOT mean "nothing of it is in
   * localStorage yet": the FIRST dirty edit of a session writes synchronously
   * (`saveNow`) and still sets the flag, every further edit is debounced
   * (`save`), and the disconnect flush (TR9) writes the latest state whenever
   * the flag is set. `false` guards the flush from clobbering a draft with an
   * un-edited (restored) document.
   */
  #hasUnflushedUserEdit = false;

  /** Guard: suppress `onUpdate` side-effects while programmatically loading content. */
  #applyingLoad = false;

  /** Element-local `rich-text-changed` debounce timer handle (TR9). */
  #changedTimer?: number;

  /**
   * Bumped by `abandonPendingInserts()` only. An image upload remembers it before
   * its `await` and inserts nothing when it changed meanwhile. Deliberately NOT
   * bumped by a new `content` reference: a host may hand the same document back
   * (an echo) or reload after a save, and the picture still belongs there.
   */
  #insertGeneration = 0;

  /** The live tiptap editor, or `undefined` in view mode (TR2/TR4). */
  get editor(): Editor | undefined {
    return this.#editor;
  }

  /** `true` once a localStorage draft was restored for the current load (OQ4). */
  get draftWasRestored(): boolean {
    return this.#draftWasRestored;
  }

  /** Imperative equivalent of `mode = 'edit'` (TR2a); idempotent. */
  enterEditMode(): void {
    if (this.mode !== "edit") {
      this.mode = "edit";
    }
  }

  /** Imperative equivalent of `mode = 'view'` (TR2a); idempotent. */
  exitEditMode(): void {
    if (this.mode !== "view") {
      this.mode = "view";
    }
  }

  /**
   * Rebase the dirty baseline to the persisted document, then reconcile the draft
   * against the LIVE editor state (TR5). Does NOT change `mode` (decoupled, TR2a).
   *
   * - genuinely clean after the rebase (no edit survived the save) →
   *   `DraftStore.clear` removes the draft key.
   * - still dirty (the user typed during the in-flight save) → `DraftStore.saveNow`
   *   synchronously rewrites the draft with the POST-SAVE live doc (debounce-bypass,
   *   with a `cancelPending` void of any trailing timer, R3-B1).
   */
  markSaved(payload?: RichTextDocument): void {
    this.#savedContentSignature = this.#signatureOf(
      payload ?? this.getSnapshot().doc,
    );
    if (this.objectId === undefined) {
      return;
    }
    const snapshot = this.getSnapshot();
    if (snapshot.isDirty) {
      this.#draftStore.saveNow(this.objectId, snapshot.doc);
    } else {
      this.#draftStore.clear(this.objectId);
    }
    this.#hasUnflushedUserEdit = false;
  }

  /**
   * Throws away the localStorage autosave draft for the current object,
   * WITHOUT touching `content`/the dirty baseline (Anke, 2026-09-16 —
   * "Abbrechen"-Taste: the host is expected to also revert its own
   * `mode`/bound `content` back to the last-saved state right after calling
   * this; that is the host's responsibility, not this element's).
   *
   * Exists because `markSaved()` is the wrong tool for a cancel: it reads
   * the LIVE (still-dirty, about-to-be-discarded) editor doc for its
   * dirty-comparison and, if still dirty against the baseline passed in,
   * calls `DraftStore.saveNow(...)` — i.e. it PERSISTS the very draft a
   * cancel needs to throw away. Without a dedicated method, the discarded
   * edit would silently reappear (`#applyLoad()` restores any present
   * draft) the next time the host re-enters edit mode on the same object.
   */
  discardDraft(): void {
    if (this.objectId === undefined) {
      return;
    }
    this.#draftStore.clear(this.objectId);
    this.#hasUnflushedUserEdit = false;
  }

  /**
   * Emits a pending `rich-text-changed` NOW instead of after the 300ms debounce
   * (TR4). For a host that is about to hand this editor to another owner, or to
   * save: the last keystrokes must reach the CURRENT owner, not arrive 300ms
   * later at whoever is bound then (open2done's issue dialog rebinds one editor
   * from issue to issue). No pending emit → nothing happens. The `#editor` check
   * is a safeguard only: a new load, view mode and disconnect cancel the timer
   * before the editor goes, so a pending emit without an editor does not occur.
   *
   * `getSnapshot()` is not enough for that host: it reads the live document but
   * reports nothing, and the timer would still fire later into the new owner.
   */
  flushChanged(): void {
    if (this.#changedTimer === undefined) {
      return;
    }
    // Cancel first: the emit runs foreign code synchronously, which must not find
    // a timer still armed.
    this.#cancelChangedTimer();
    if (this.#editor) {
      this.#emitChanged();
    }
  }

  /**
   * Drops every image upload still in flight: when it finishes, it inserts
   * nothing (a `console.info` names the dropped picture — the uploaded asset
   * stays on the server without a reference). For a host that hands this editor
   * to another owner while an upload runs; otherwise the picture would land in
   * the next owner's document. A new `content` reference alone does NOT do this
   * (see `#insertGeneration`), because two owners may well share the same text.
   */
  abandonPendingInserts(): void {
    this.#insertGeneration++;
  }

  /**
   * Synchronous snapshot that bypasses the 300ms `rich-text-changed` debounce
   * (TR5). Reads the LIVE `editor.getJSON()` directly (never the cached event
   * detail), so a save-button click that immediately follows a keystroke persists
   * the true current document. In view mode it falls back to `content`.
   *
   * Without an editor (view mode) nothing is being edited, so `isDirty` is always
   * `false`: `doc` is `content` there, and unsaved work lives in the draft.
   * Comparing against the saved signature instead would read dirty for every
   * unedited viewer (the signature starts as `""`) and after `markSaved(payload)`
   * with a payload that differs from `content`.
   */
  getSnapshot(): { doc: RichTextDocument; isDirty: boolean } {
    const doc = this.#editor
      ? (this.#editor.getJSON() as unknown as RichTextDocument)
      : (this.content ?? EMPTY_DOC);
    return {
      doc,
      isDirty: this.#editor
        ? this.#signatureOf(doc) !== this.#savedContentSignature
        : false,
    };
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    // A new content reference (Lit identity change) OR a (late) objectId is a new
    // load: re-evaluate the restore/baseline in `updated()`. A reference-equal
    // content re-assignment never reaches here (Lit short-circuits it) — that IS
    // the baseline short-circuit (TR2).
    if (changed.has("content") || changed.has("objectId")) {
      this.#loadApplied = false;
      // A pending emit would read the editor when it fires — by then the freshly
      // loaded (tiptap-normalised) document — and report it as a user change,
      // dirtying a clean owner. Whoever wants the last keystrokes calls
      // `flushChanged()` before handing in new content.
      this.#cancelChangedTimer();
    }
    if (changed.has("content")) {
      this.#draftWasRestored = false;
    }
  }

  protected override updated(changed: PropertyValues<this>): void {
    if (changed.has("mode")) {
      if (this.mode === "edit") {
        this.#mountEditor();
      } else {
        this.#destroyEditor();
      }
    }
    if (this.#editor && !this.#loadApplied) {
      this.#applyLoad();
    }
  }

  override disconnectedCallback(): void {
    // TR9: flush a pending draft-save synchronously BEFORE clearing timers, so the
    // last keystrokes survive a reload. No-op unless an unflushed edit exists for a
    // set objectId (never overwrite an existing draft with an un-edited load).
    if (
      this.#hasUnflushedUserEdit &&
      this.objectId !== undefined &&
      this.#editor
    ) {
      this.#draftStore.saveNow(this.objectId, this.getSnapshot().doc);
      this.#hasUnflushedUserEdit = false;
    }
    // TR9: clear the draft timer through the single cancel path (TR6 (d)) and the
    // element-local event-debounce timer, then tear the editor down.
    if (this.objectId !== undefined) {
      this.#draftStore.cancelPending(this.objectId);
    }
    this.#destroyEditor();
    super.disconnectedCallback();
  }

  protected override render(): TemplateResult {
    if (this.mode === "view") {
      return this.renderView();
    }
    // Edit mode: a package-internal formatting toolbar above the editor host
    // (O2D.P-PRD-019). The toolbar buttons bind to the tiptap commands already
    // registered in RICH_TEXT_EXTENSIONS and reflect the active mark/node state.
    // The editor's ProseMirror `contenteditable` host is appended into the static
    // `.o2d-rte-editor` div imperatively (`#mountEditor`); Lit never diffs its
    // children (TR4/TR9).
    return html`
      ${this.#renderToolbar()}
      <div class="o2d-rte-editor"></div>
    `;
  }

  /**
   * Render the edit-mode formatting toolbar (O2D.P-PRD-019), grouped by kind.
   * Group order (Anke, 2026-10-03): Schriftgröße | marks | case | lists |
   * alignment | Bild + Linie | Schriftfarbe | Hinterlegung | Tabelle (+ the
   * table-editing buttons while the caret is in a table). Toggle buttons run a
   * no-arg chainable tiptap command, the size buttons `setMark` with their
   * value; toggles and sizes reflect the live active state via an `is-active`
   * class. Active-state reads are `#editor`-optional-chained, so the toolbar
   * renders inert (all inactive) in the first edit-render cycle BEFORE the
   * editor is mounted in `updated()` — a `transaction` subscription then
   * re-renders it.
   */
  #renderToolbar(): TemplateResult {
    return html`
      <div class="o2d-rte-toolbar" role="toolbar" aria-label="Textformatierung">
        <div class="o2d-rte-toolbar__group" role="group" aria-label="Schriftgröße">
          ${FONT_SIZES.map((s) => this.#renderFontSizeButton(s))}
        </div>
        <div class="o2d-rte-toolbar__group">
          ${MARK_BUTTONS.map((b) => this.#renderToggleButton(b))}
        </div>
        <div class="o2d-rte-toolbar__group">
          ${CASE_BUTTONS.map((b) => this.#renderOneShotButton(b))}
        </div>
        <div class="o2d-rte-toolbar__group">
          ${LIST_BUTTONS.map((b) => this.#renderToggleButton(b))}
        </div>
        <div class="o2d-rte-toolbar__group">
          ${ALIGN_BUTTONS.map((b) => this.#renderAlignButton(b))}
        </div>
        <div class="o2d-rte-toolbar__group">
          ${this.#renderInsertButton("Bild einfügen (oder mit Strg+V einfügen)", "o2d-rte-toolbar__btn--image", this.#pickImage)}
          ${this.#renderInsertButton("Horizontale Linie einfügen", "o2d-rte-toolbar__btn--horizontal-rule", this.#insertHorizontalRule)}
        </div>
        ${this.#renderSwatches("Schriftfarbe", TEXT_COLORS, (c) => this.#setColor(c), () => this.#unsetColor())}
        ${this.#renderSwatches("Hinterlegung", HIGHLIGHT_COLORS, (c) => this.#setHighlight(c), () => this.#unsetHighlight())}
        <div class="o2d-rte-toolbar__group">
          ${this.#renderInsertButton("Tabelle einfügen", "o2d-rte-toolbar__btn--table", this.#insertTable)}
          ${this.#editor?.isActive("table")
            ? TABLE_EDIT_BUTTONS.map((b) => this.#renderOneShotButton(b))
            : null}
        </div>
      </div>
    `;
  }

  /** Render one one-shot button (no active state). A `needsSelection` button
   *  is disabled while nothing is selected; the `transaction` listener
   *  re-renders on every selection move, so the state follows the caret. */
  #renderOneShotButton(button: OneShotButton): TemplateResult {
    const disabled =
      button.needsSelection === true &&
      (this.#editor?.state.selection.empty ?? true);
    return html`
      <button
        type="button"
        class="o2d-rte-toolbar__btn"
        title=${button.title}
        ?disabled=${disabled}
        @mousedown=${this.#preventBlur}
        @click=${() => this.#runOneShot(button.command)}
      >
        ${button.label}
      </button>
    `;
  }

  /** Run a no-arg chainable one-shot command against the live editor. */
  #runOneShot(command: OneShotCommand): void {
    this.#editor?.chain().focus()[command]().run();
  }

  /**
   * Render one icon-only insert button (Bild / Tabelle / Linie). One-shot like
   * {@link #renderOneShotButton}, but its action is a handler of this element
   * (file picker, sized table, rule) rather than a no-arg chain command, and
   * it has no active state. Icon glyph: see {@link ToggleButton.iconClass}.
   */
  #renderInsertButton(title: string, iconClass: string, action: () => void): TemplateResult {
    return html`
      <button
        type="button"
        class="o2d-rte-toolbar__btn ${iconClass}"
        title=${title}
        aria-label=${title}
        @mousedown=${this.#preventBlur}
        @click=${action}
      ></button>
    `;
  }

  /** Render one toggle button from its descriptor — always icon-only (see
   *  {@link ToggleButton.iconClass}). */
  #renderToggleButton(button: ToggleButton): TemplateResult {
    const active = this.#editor?.isActive(button.activeName) ?? false;
    return html`
      <button
        type="button"
        class="o2d-rte-toolbar__btn ${active ? "is-active" : ""} ${button.iconClass}"
        aria-pressed=${active}
        title=${button.title}
        aria-label=${button.title}
        @mousedown=${this.#preventBlur}
        @click=${() => this.#runToggle(button.command)}
      ></button>
    `;
  }

  /**
   * Render one text-alignment button. Active when the current block's
   * `text-align` equals this button's value (TextAlign stores alignment as a
   * block-node attribute, so `isActive({ textAlign })` is the correct probe).
   */
  #renderAlignButton(button: AlignButton): TemplateResult {
    const active = this.#editor?.isActive({ textAlign: button.align }) ?? false;
    return html`
      <button
        type="button"
        class="o2d-rte-toolbar__btn ${active ? "is-active" : ""} ${button.iconClass}"
        aria-pressed=${active}
        title=${button.title}
        aria-label=${button.title}
        @mousedown=${this.#preventBlur}
        @click=${() => this.#setAlign(button.align)}
      >
      </button>
    `;
  }

  /** Set the current block's horizontal alignment and keep the caret in the editor. */
  #setAlign(align: TextAlignment): void {
    this.#editor?.chain().focus().setTextAlign(align).run();
  }

  /** Apply a text colour to the selection (Color extension) and keep focus. */
  #setColor(color: string): void {
    this.#editor?.chain().focus().setColor(color).run();
  }

  /** Remove the text colour from the selection. */
  #unsetColor(): void {
    this.#editor?.chain().focus().unsetColor().run();
  }

  /** Apply a highlight (Hinterlegung) colour to the selection and keep focus. */
  #setHighlight(color: string): void {
    this.#editor?.chain().focus().setHighlight({ color }).run();
  }

  /** Remove the highlight from the selection. */
  #unsetHighlight(): void {
    this.#editor?.chain().focus().unsetHighlight().run();
  }

  /**
   * Render one colour-swatch group: the preset colours as clickable squares plus
   * a "remove colour" button. `apply` sets the colour, `clear` removes it; both
   * run against the live selection. `mousedown` preventDefault holds the caret
   * (see {@link #preventBlur}) so the colour lands on the intended range.
   */
  #renderSwatches(
    label: string,
    colors: readonly string[],
    apply: (color: string) => void,
    clear: () => void,
  ): TemplateResult {
    return html`
      <div class="o2d-rte-toolbar__group" role="group" aria-label=${label}>
        <span class="o2d-rte-toolbar__label" aria-hidden="true">${label}</span>
        ${colors.map(
          (c) => html`
            <button
              type="button"
              class="o2d-rte-swatch"
              style=${`background:${c}`}
              title=${`${label}: ${c}`}
              aria-label=${`${label} ${c}`}
              @mousedown=${this.#preventBlur}
              @click=${() => apply(c)}
            ></button>
          `,
        )}
        <button
          type="button"
          class="o2d-rte-toolbar__btn"
          title=${`${label} entfernen`}
          @mousedown=${this.#preventBlur}
          @click=${clear}
        >
          &times;
        </button>
      </div>
    `;
  }

  /**
   * Render one Schriftgröße button (S / M / L / XL). Active — like a toggle
   * button — when the whole selection carries this step (no size = "Normal");
   * a selection mixing sizes lights none ({@link selectionFontSize}). A click
   * applies the step to the selection; {@link #preventBlur} keeps the caret,
   * as for every toolbar button.
   */
  #renderFontSizeButton(size: FontSizeOption): TemplateResult {
    const current = this.#editor ? selectionFontSize(this.#editor.state) : null;
    const active = size.value === current;
    const title = `Schriftgröße: ${size.label}`;
    return html`
      <button
        type="button"
        class="o2d-rte-toolbar__btn ${active ? "is-active" : ""}"
        aria-pressed=${active}
        title=${title}
        aria-label=${title}
        @mousedown=${this.#preventBlur}
        @click=${() => this.#setFontSize(size.value)}
      >
        ${size.short}
      </button>
    `;
  }

  /** Apply a Schriftgröße step to the selection. The empty value ("Normal")
   *  clears the size; a `textStyle` mark left without any attribute is then
   *  dropped, so no empty `<span>` is stored. `removeEmptyTextStyle` covers a
   *  range selection (marks in the document); at a bare caret `setMark` only
   *  stores the mark for the next typed character, so an empty stored mark is
   *  removed separately (review 0031/02). A colour stays: its mark is not empty. */
  #setFontSize(value: string): void {
    this.#editor
      ?.chain()
      .focus()
      .setMark("textStyle", { fontSize: value || null })
      .removeEmptyTextStyle()
      .command(({ tr }) => {
        const stored = tr.storedMarks?.find((m) => m.type.name === "textStyle");
        if (stored && !Object.values(stored.attrs).some((v) => !!v)) {
          tr.removeStoredMark(stored.type);
        }
        return true;
      })
      .run();
  }

  /**
   * Insert a horizontal rule (`<hr>`) after the current block. The node comes
   * from tiptap's HorizontalRule extension — MIT-licensed and already part of
   * StarterKit, which `RICH_TEXT_EXTENSIONS` uses (so typing `---` — or
   * `___ ` / `*** ` — on an empty line creates one too, and view mode renders
   * it via `generateHTML`). Icon-only toolbar button like "Bild"/"Tabelle"
   * (`o2d-rte-toolbar__btn--horizontal-rule`, see {@link #renderInsertButton}).
   */
  #insertHorizontalRule = (): void => {
    this.#editor?.chain().focus().setHorizontalRule().run();
  };

  /**
   * Insert a default 3x3 table with a header row at the caret and keep focus in
   * the editor. One-shot (no active state). Bound as a field so it can be used
   * directly as the `@click` handler.
   */
  #insertTable = (): void => {
    this.#editor
      ?.chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  /**
   * Insert an image from a `File` — the shared path for paste (Strg+V) and the
   * toolbar picker. With an {@link imageUploadHandler} the file is uploaded and
   * the returned asset URL becomes the image `src` (so it lands in the host's
   * file store); without one the image is embedded as an inline base64 data-URL.
   * A rejected upload inserts NOTHING (no silent base64 fallback — that would
   * defeat the seam; mirrors @o2d/whiteboard). Public so a host can also insert
   * programmatically. Async but self-contained and guarded (TR9): the editor may
   * be torn down during the await, so the final insert is optional-chained.
   * `abandonPendingInserts()` during the await drops the picture (logged with
   * `console.info`; the uploaded asset then stays on the server unreferenced).
   */
  async insertImageFile(file: File): Promise<void> {
    if (!this.#editor) {
      return;
    }
    await this.#insertImage(file, this.#insertGeneration);
  }

  /**
   * Shared tail of every image insert. `generation` is taken by the CALLER before its first
   * await, so an owner change during any wait (host hook or upload) drops the picture instead
   * of inserting it into the next owner's document. With `resolvedSrc` the host already holds
   * the image: nothing is uploaded, and an unsafe `src` inserts nothing.
   */
  async #insertImage(file: File, generation: number, resolvedSrc?: string): Promise<void> {
    let src: string | null;
    if (resolvedSrc !== undefined) {
      src = resolvedSrc;
    } else if (this.imageUploadHandler) {
      try {
        const result = await this.imageUploadHandler(file);
        src = result?.src ?? null;
      } catch (err) {
        console.error("[o2d-rich-text-editor] image upload failed", err);
        return;
      }
    } else {
      src = await readFileAsDataUrl(file);
    }
    if (!src) {
      return;
    }
    if (!isSafeImageSrc(src)) {
      console.error("[o2d-rich-text-editor] refused unsafe image src");
      return;
    }
    if (generation !== this.#insertGeneration) {
      // Only the start of `src`: without an upload handler it is a whole base64 picture.
      console.info("[o2d-rich-text-editor] image not inserted: the editor changed owner during the upload", {
        src: src.slice(0, 120),
      });
      return;
    }
    this.#editor?.chain().focus().setImage({ src }).run();
  }

  /**
   * Pasted image + text: ask the host first (see {@link PastedImageResolver}). A rejecting or
   * empty answer falls back to the upload; an owner change while the host answers stops here,
   * before any upload, so no unreferenced asset is left behind.
   */
  async #resolveAndInsert(resolver: PastedImageResolver, text: string, file: File): Promise<void> {
    if (!this.#editor) {
      return;
    }
    const generation = this.#insertGeneration;
    let resolved: string | undefined;
    try {
      const answer: unknown = await resolver(text, file);
      const src = (answer as { src?: unknown } | null)?.src;
      resolved = typeof src === "string" ? src : undefined;
    } catch (err) {
      console.error("[o2d-rich-text-editor] pastedImageResolver failed — uploading the pasted image instead", err);
    }
    if (generation !== this.#insertGeneration || !this.#editor) {
      console.info("[o2d-rich-text-editor] pasted image dropped: the editor changed owner while the host answered");
      return;
    }
    await this.#insertImage(file, generation, resolved);
  }

  /** Open a native file picker and insert the chosen image (toolbar button). */
  #pickImage = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file) {
        void this.insertImageFile(file);
      }
    });
    input.click();
  };

  /**
   * Keep the editor selection while a toolbar button is pressed. `mousedown` on a
   * button would otherwise blur the `contenteditable` and collapse the selection
   * before the command runs; `preventDefault` holds the caret/selection so the
   * command applies to the intended range.
   */
  #preventBlur = (event: MouseEvent): void => {
    event.preventDefault();
  };

  /**
   * Run a no-arg chainable tiptap toggle command against the live editor and
   * refocus, so the toggle applies to the current selection and the caret stays
   * in the editor. No-op in view mode (no editor).
   */
  #runToggle(command: ToggleCommand): void {
    this.#editor?.chain().focus()[command]().run();
  }

  /**
   * Render the current `content` read-only via tiptap `generateHTML` (TR3). An
   * empty/absent document renders nothing. The readonly {@link RichTextDocument} is
   * cast at the tiptap boundary; the public type is NOT widened.
   */
  private renderView(): TemplateResult {
    const doc = this.content;
    if (!doc || !Array.isArray(doc.content) || doc.content.length === 0) {
      return html``;
    }
    const rendered = generateHTML(
      doc as unknown as Parameters<typeof generateHTML>[0],
      RICH_TEXT_EXTENSIONS,
    );
    return html`${unsafeHTML(rendered)}`;
  }

  /**
   * Mount the tiptap `Editor` into the rendered Light-DOM host (TR4, whiteboard
   * idiom `textbox-editor.controller.ts:636`). Idempotent — a no-op if already
   * mounted. The initial document is `content` (the draft, if any, is applied by
   * {@link #applyLoad} in the same update cycle). Content edits drive
   * {@link #onEditorUpdate}.
   */
  #mountEditor(): void {
    if (this.#editor) {
      return;
    }
    const host = this.querySelector<HTMLElement>(".o2d-rte-editor");
    if (!host) {
      return;
    }
    this.#editor = new Editor({
      element: host,
      // `TextCase` adds commands only (no schema), so the edit schema stays the
      // one `generateHTML` renders with in view mode. It lives here and not in
      // `createRichTextExtensions()`, which the whiteboard and the open2done
      // converter share.
      extensions: [...RICH_TEXT_EXTENSIONS, TextCase],
      // TR4/TR8: readonly structural doc cast at the tiptap boundary (the editor
      // only reads `content`); the public type is NOT widened.
      content: (this.content ?? EMPTY_DOC) as unknown as Record<
        string,
        unknown
      >,
      editorProps: {
        // Intercept pasted image files (Strg+V) — an async path (TR9). We stop
        // the default paste, then fire-and-forget the host hook and/or the
        // upload+insert so the ProseMirror handler stays synchronous; the image
        // lands once they resolve. Non-image pastes fall through to tiptap's
        // built-in handling untouched.
        handlePaste: (_view, event) => {
          const file = imageFileFromClipboard(event);
          if (!file) {
            return false;
          }
          event.preventDefault();
          // Read now: `clipboardData` is unreadable once the event is over.
          const text = event.clipboardData?.getData("text/plain") ?? "";
          const resolver = this.pastedImageResolver;
          if (resolver && text.trim() !== "") {
            void this.#resolveAndInsert(resolver, text, file);
          } else {
            void this.insertImageFile(file);
          }
          return true;
        },
        // An editor never sets a font size in absolute units — the text reads at
        // the size of the application around it. A paste from Word, Docs or a
        // browser carries e.g. `font-size: 18pt`; that value is dropped here,
        // while bold, italic, colour, links and block structure survive
        // untouched. This sits in the ELEMENT on purpose: the `FontSize`
        // extension is shared with @o2d/whiteboard, whose textbox stores and
        // exports absolute pixel sizes on the same mark (see
        // `controllers/paste-font-size.ts` for the measurement).
        transformPasted: (slice) => stripAbsoluteFontSizes(slice),
      },
    });
    this.#editor.on("update", () => this.#onEditorUpdate());
    // O2D.P-PRD-019: re-render the toolbar whenever the selection or document
    // changes, so each button's active state (`isActive`) reflects the caret's
    // current marks/nodes. `transaction` fires for both content edits and pure
    // selection moves; `requestUpdate` re-renders only the Lit toolbar (the
    // imperatively-owned editor host children are never diffed, TR4/TR9).
    // Skip the programmatic load/mount transactions (`#applyingLoad`): those fire
    // synchronously inside Lit's `updated()` and would schedule a redundant
    // update-in-update (a real user selection/edit refreshes the state after).
    this.#editor.on("transaction", () => {
      // Switching a focused editor to view mode removes its contenteditable in
      // render(); the browser then fires blur and tiptap dispatches a blur
      // transaction while `mode` is already "view" and the editor is not yet
      // destroyed (that happens later in updated()). Requesting an update there
      // schedules a second Lit update from inside the first one. The toolbar
      // state this refresh serves only exists in edit mode.
      if (!this.#applyingLoad && this.mode === "edit") {
        this.requestUpdate();
      }
    });
    // TR9 (async-path safety): the image paste above is this element's async
    // paste path — the host hook (`pastedImageResolver`) and the upload
    // (`imageUploadHandler`). Both are wrapped in try/catch and guarded by the
    // insert generation; any further async paste path MUST be guarded the same
    // way to avoid a silent failure.
  }

  /**
   * Tear down the tiptap editor (TR9), mirroring the whiteboard
   * `destroy()`+`remove()` idiom (`textbox-editor.controller.ts:938-949`):
   * `editor.destroy()` frees the ProseMirror view + listeners (no leak), THEN
   * the injected `contenteditable` host node is detached explicitly so NO
   * `contenteditable` survives an `exitEditMode()`/`mode="view"` swap —
   * `destroy()` alone does not guarantee the host leaves the DOM. On a mode
   * swap the Lit template re-render (`render()` branches to the view path)
   * already detaches the host; this explicit `remove()` is the robust
   * belt-and-suspenders the whiteboard proved necessary (a detached node's
   * `remove()` is a harmless no-op).
   */
  #destroyEditor(): void {
    // A pending emit after teardown would report `content` (the loaded document,
    // not the edit) as a change. Covers the switch to view mode and disconnect.
    this.#cancelChangedTimer();
    if (!this.#editor) {
      return;
    }
    const editor = this.#editor;
    // Capture the injected host node BEFORE destroy() tears down `editor.view`,
    // so it can still be detached afterwards (whiteboard `remove()` step).
    const editorDom = editor.view?.dom as HTMLElement | undefined;
    this.#editor = undefined;
    this.#loadApplied = false;
    try {
      editor.destroy();
    } catch {
      // Defensive: tiptap can throw if the view was already torn down by a
      // concurrent path — swallow so teardown continues (whiteboard idiom).
    }
    if (editorDom?.parentNode) {
      editorDom.remove();
    }
  }

  /**
   * Apply the current (content, objectId) load to the mounted editor exactly once
   * per load (TR6). Restores a per-object draft when present (editor doc = draft,
   * baseline = persisted `content` → the recovered work reads dirty), otherwise
   * confirms `content`. Ordering-safe: runs only once BOTH content and objectId are
   * present (a late objectId re-triggers it via `willUpdate`).
   */
  #applyLoad(): void {
    if (!this.#editor) {
      return;
    }
    // Establish the baseline from the NORMALIZED editor document (tiptap adds
    // default node/mark attributes on load), so an unedited load reads clean —
    // comparing the raw input against `editor.getJSON()` would falsely be dirty.
    this.#setEditorContent(this.content ?? EMPTY_DOC);
    this.#savedContentSignature = this.#signatureOf(
      this.#editor.getJSON() as unknown as RichTextDocument,
    );
    // Overlay the draft, if present. The baseline stays the persisted content
    // (never the draft, TR6) — a diverging restored draft must read dirty.
    const draft = this.#loadDraft();
    if (draft) {
      this.#setEditorContent(draft);
      this.#draftWasRestored = true;
      this.fireEvent<RichTextDraftRestoredEventDetail>(
        RICH_TEXT_DRAFT_RESTORED_EVENT,
        { objectId: this.objectId as string },
      );
    }
    this.#loadApplied = true;
    this.#hasUnflushedUserEdit = false;
  }

  /**
   * Load this object's draft, or `undefined` when the draft is disabled (no
   * objectId), absent, or corrupt. A corrupt draft resolves to `undefined` inside
   * {@link DraftStore.load} (no throw) → the `content` fallback.
   */
  #loadDraft(): RichTextDocument | undefined {
    if (this.objectId === undefined) {
      return undefined;
    }
    return this.#draftStore.load(this.objectId);
  }

  /**
   * Replace the editor document without emitting an update (load, not a keystroke).
   *
   * The load is kept out of the undo history (`addToHistory: false`). Otherwise
   * Ctrl+Z right after opening would restore the document as it was before the
   * block ids were assigned, UniqueID would mint new ids, and the element would
   * report an edit the user never made.
   */
  #setEditorContent(doc: RichTextDocument): void {
    if (!this.#editor) {
      return;
    }
    this.#applyingLoad = true;
    try {
      this.#editor
        .chain()
        .setMeta("addToHistory", false)
        .setContent(doc as unknown as Content, { emitUpdate: false })
        .run();
      this.#assignMissingBlockIds();
      this.#startInText();
    } finally {
      this.#applyingLoad = false;
    }
  }

  /**
   * Put the caret into text when a load leaves a whole node selected.
   *
   * Measured 2026-09-21: a document that starts or ends with an image loads with
   * that image node-selected. Typing that reaches the editor in this state can
   * replace the image (see `ClickReleasesNodeSelection`). The caret goes to the
   * first position that can hold text instead, which may be an empty paragraph. A
   * document without any textblock keeps its selection. This runs only for loads
   * through this element; other users of the extension list load their content
   * themselves and are not covered by it.
   *
   * Selection only: the document is unchanged, so this adds no undo step and
   * emits no update even without the two metas below, which only mirror
   * `#assignMissingBlockIds`.
   */
  #startInText(): void {
    const editor = this.#editor;
    if (!editor || !(editor.state.selection instanceof NodeSelection)) {
      return;
    }
    const firstText = Selection.findFrom(editor.state.doc.resolve(0), 1, true);
    if (!firstText) {
      return;
    }
    const tr = editor.state.tr.setSelection(firstText);
    tr.setMeta("addToHistory", false);
    tr.setMeta("preventUpdate", true);
    editor.view.dispatch(tr);
  }

  /**
   * Give every block of the loaded document that lacks a block id its id, before
   * the dirty baseline is taken from `getJSON()`.
   *
   * UniqueID alone assigns these ids in its `onCreate` hook, which tiptap runs
   * deferred (after a `setTimeout`). That late transaction would emit an update,
   * so an unedited document would fire `rich-text-changed`, write a draft and
   * read dirty. This applies when the loaded document equals the one the editor
   * was mounted with. When it differs (the empty document, whose mounted state
   * has no paragraph yet, or a restored draft), UniqueID already assigns the ids
   * inside the load transaction and this method finds nothing left to do.
   *
   * Uses the generator configured on the UniqueID extension, so the ids have the
   * same format as ids minted while typing. No-op when block ids are disabled.
   */
  #assignMissingBlockIds(): void {
    const editor = this.#editor;
    const uniqueId = editor?.extensionManager.extensions.find(
      (extension) => extension.name === "uniqueID",
    );
    if (!editor || !uniqueId) {
      return;
    }
    const { attributeName, types, generateID } = uniqueId.options as {
      attributeName: string;
      types: string[];
      generateID: (context: { node: ProseMirrorNode; pos: number }) => string;
    };
    const { tr } = editor.state;
    editor.state.doc.descendants((node, pos) => {
      if (types.includes(node.type.name) && !node.attrs[attributeName]) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          [attributeName]: generateID({ node, pos }),
        });
      }
    });
    if (!tr.docChanged) {
      return;
    }
    tr.setMeta("addToHistory", false);
    tr.setMeta("preventUpdate", true);
    editor.view.dispatch(tr);
  }

  /**
   * tiptap `onUpdate` handler (TR4/TR6): a real user edit. Debounces the
   * `rich-text-changed` emit and the per-object draft save, and marks the edit
   * unflushed. Suppressed while programmatically loading content.
   *
   * Bug fix (Anke, 2026-09-18 — horstplan's sidebar "unsaved" indicator
   * failing to go italic on a fresh edit): a host listening for
   * `rich-text-changed` and reacting by re-reading `DraftStore.load()`
   * synchronously (horstplan's `KnowledgeDraftChangeNotifier`) races ahead
   * of this store's OWN 500ms debounce — `#scheduleChanged()`'s 300ms
   * `rich-text-changed` emit lands well before that write does, so the very
   * first read after starting to type finds nothing yet, and nothing ever
   * re-notifies once the debounced write actually completes. The FIRST
   * dirty keystroke of an edit session (`!#hasUnflushedUserEdit`) now
   * writes via `saveNow` — bypassing the debounce — so any host reacting to
   * the very next `rich-text-changed` emit already finds the draft in
   * place. Subsequent keystrokes in the same, already-unflushed session
   * keep debouncing as before (TR6) — this only closes the race on the
   * session's opening edit.
   */
  #onEditorUpdate(): void {
    if (this.#applyingLoad) {
      return;
    }
    this.#scheduleChanged();
    if (this.objectId !== undefined) {
      const snapshot = this.getSnapshot();
      if (snapshot.isDirty && !this.#hasUnflushedUserEdit) {
        this.#draftStore.saveNow(this.objectId, snapshot.doc);
      } else {
        this.#draftStore.save(this.objectId, snapshot.doc);
      }
      this.#hasUnflushedUserEdit = true;
    }
  }

  /** (Re)arm the 300ms `rich-text-changed` debounce (TR4). */
  #scheduleChanged(): void {
    this.#cancelChangedTimer();
    this.#changedTimer = window.setTimeout(() => {
      this.#changedTimer = undefined;
      this.#emitChanged();
    }, CHANGED_DEBOUNCE_MS);
  }

  /** Clears the `rich-text-changed` debounce timer, if one is armed. */
  #cancelChangedTimer(): void {
    if (this.#changedTimer !== undefined) {
      window.clearTimeout(this.#changedTimer);
      this.#changedTimer = undefined;
    }
  }

  /** Emit `rich-text-changed` with the live document + dirty flag (TR4). */
  #emitChanged(): void {
    const snapshot = this.getSnapshot();
    this.fireEvent<RichTextChangedEventDetail>(RICH_TEXT_CHANGED_EVENT, {
      doc: snapshot.doc,
      isDirty: snapshot.isDirty,
    });
  }

  /** Stable content signature for the dirty comparison (TR4/TR5). */
  #signatureOf(doc: RichTextDocument | undefined): string {
    return JSON.stringify(doc ?? EMPTY_DOC);
  }
}
